import React, { useState, useEffect } from 'react';
import {
  permissionApi,
  PagePermission,
  EmployeeWithPermissions,
  PermissionType,
} from '../services/permissionApi';
import { useAlert } from '../contexts/AlertContext';

const ROLE_LABELS: Record<string, string> = {
  DEVELOPER: '개발자',
  ADMIN: '관리자',
  PROJECT_MANAGER: '프로젝트 매니저',
  EMPLOYEE: '일반 직원',
};

const ROLE_OPTIONS = ['DEVELOPER', 'ADMIN', 'PROJECT_MANAGER', 'EMPLOYEE'];

const PermissionManagement: React.FC = () => {
  const { showToast } = useAlert();
  const [activeTab, setActiveTab] = useState<'employees' | 'pages'>('employees');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 데이터 상태
  const [employees, setEmployees] = useState<EmployeeWithPermissions[]>([]);
  const [pagePermissions, setPagePermissions] = useState<PagePermission[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithPermissions | null>(null);

  // 모달 상태
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState<EmployeeWithPermissions | null>(null);
  const [newRole, setNewRole] = useState<string>('');

  // 필터 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (selectedEmployeeId?: number) => {
    try {
      setLoading(true);
      const [employeesData, pagesData] = await Promise.all([
        permissionApi.getEmployeesForPermission(),
        permissionApi.getAllPagePermissions(),
      ]);
      setEmployees(employeesData);
      setPagePermissions(pagesData);

      // 모달이 열려있을 때 selectedEmployee도 업데이트
      if (selectedEmployeeId) {
        const updatedEmployee = employeesData.find(emp => emp.id === selectedEmployeeId);
        if (updatedEmployee) {
          setSelectedEmployee(updatedEmployee);
        }
      }
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
      showToast('데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 권한 변경
  const handleRoleChange = async () => {
    if (!roleChangeTarget || !newRole) return;
    try {
      await permissionApi.updateEmployeeRole(roleChangeTarget.id, newRole);
      setIsRoleModalOpen(false);
      setRoleChangeTarget(null);
      setNewRole('');
      fetchData();
    } catch (err) {
      showToast('권한 변경에 실패했습니다.', 'error');
    }
  };

  // 개별 권한 추가
  const handleAddPermission = async (employeeId: number, path: string, type: PermissionType) => {
    try {
      await permissionApi.createEmployeePermission({
        employeeId,
        path,
        permissionType: type,
      });
      fetchData(employeeId); // 해당 직원 ID로 selectedEmployee 업데이트
    } catch (err) {
      showToast('권한 추가에 실패했습니다.', 'error');
    }
  };

  // 개별 권한 삭제
  const handleRemovePermission = async (employeeId: number, path: string) => {
    try {
      await permissionApi.deleteEmployeePermissionByPath(employeeId, path);
      fetchData(employeeId); // 해당 직원 ID로 selectedEmployee 업데이트
    } catch (err) {
      showToast('권한 삭제에 실패했습니다.', 'error');
    }
  };

  // 페이지 권한 수정
  const handleUpdatePagePermission = async (id: number, allowedRoles: string[]) => {
    try {
      await permissionApi.updatePagePermission(id, { allowedRoles });
      fetchData();
    } catch (err) {
      showToast('권한 수정에 실패했습니다.', 'error');
    }
  };

  // 필터링된 직원 목록
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch =
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.empNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || emp.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // 직원의 특정 페이지에 대한 개별 권한 찾기
  const getIndividualPermission = (
    emp: EmployeeWithPermissions,
    path: string
  ): PermissionType | null => {
    const perm = emp.individualPermissions.find((p) => p.path === path);
    return perm ? perm.permissionType : null;
  };

  // 직원의 특정 페이지 접근 가능 여부 확인
  const hasPageAccess = (emp: EmployeeWithPermissions, page: PagePermission): boolean => {
    const individual = getIndividualPermission(emp, page.path);
    if (individual === PermissionType.DENY) return false;
    if (individual === PermissionType.ALLOW) return true;
    return page.allowedRoles.includes(emp.role);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        {error}
        <button onClick={() => fetchData()} className="ml-4 text-blue-600 underline">
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">권한 관리</h1>
      </div>

      {/* 탭 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'employees'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            직원별 권한
          </button>
          <button
            onClick={() => setActiveTab('pages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            페이지별 권한
          </button>
        </nav>
      </div>

      {/* 직원별 권한 탭 */}
      {activeTab === 'employees' && (
        <div className="space-y-4">
          {/* 필터 */}
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="이름, 사번, 이메일 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-40 pl-4 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">모든 권한</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>

          {/* 직원 테이블 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    직원
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    권한 상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    페이지별 권한
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    권한 관리
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                        <div className="text-sm text-gray-500">
                          {emp.empNo} | {emp.position} | {emp.department}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          emp.role === 'ADMIN'
                            ? 'bg-red-100 text-red-800'
                            : emp.role === 'DEVELOPER'
                            ? 'bg-purple-100 text-purple-800'
                            : emp.role === 'PROJECT_MANAGER'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {ROLE_LABELS[emp.role] || emp.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {emp.individualPermissions.length === 0 ? (
                          <span className="text-gray-400 text-sm">없음</span>
                        ) : (
                          emp.individualPermissions.map((perm) => (
                            <span
                              key={perm.id}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                perm.permissionType === PermissionType.ALLOW
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {perm.path}
                              <button
                                onClick={() => handleRemovePermission(emp.id, perm.path)}
                                className="ml-1 hover:text-red-600"
                              >
                                x
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setRoleChangeTarget(emp);
                          setNewRole(emp.role);
                          setIsRoleModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        권한 변경
                      </button>
                      <button
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setIsPermissionModalOpen(true);
                        }}
                        className="text-green-600 hover:text-green-900"
                      >
                        페이지별 권한 설정
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 페이지별 권한 탭 */}
      {activeTab === 'pages' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    페이지
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    경로
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    허용된 역할
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagePermissions.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{page.name}</div>
                        <div className="text-sm text-gray-500">{page.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {page.path}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {ROLE_OPTIONS.map((role) => {
                          const isAllowed = page.allowedRoles.includes(role);
                          return (
                            <button
                              key={role}
                              onClick={() => {
                                const newRoles = isAllowed
                                  ? page.allowedRoles.filter((r) => r !== role)
                                  : [...page.allowedRoles, role];
                                handleUpdatePagePermission(page.id, newRoles);
                              }}
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                isAllowed
                                  ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              }`}
                            >
                              {ROLE_LABELS[role]}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          page.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {page.isActive ? '활성' : '비활성'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 권한 변경 모달 */}
      {isRoleModalOpen && roleChangeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">권한 변경</h3>
            <p className="text-sm text-gray-600 mb-4">
              {roleChangeTarget.name}님의 권한을 변경합니다.
            </p>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsRoleModalOpen(false);
                  setRoleChangeTarget(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleRoleChange}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                변경
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 페이지별 권한 설정 모달 */}
      {isPermissionModalOpen && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[600px] max-h-[80vh] flex flex-col">
            {/* 헤더 */}
            <div className="p-6 pb-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-2">페이지별 권한 설정</h3>
              <p className="text-sm text-gray-600">
                {selectedEmployee.name}님의 페이지별 개별 권한을 설정합니다.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                * 페이지별 권한은 권한 기반 권한보다 우선합니다.
              </p>
            </div>

            {/* 콘텐츠 (스크롤 영역) */}
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              <div className="space-y-2">
                {pagePermissions.map((page) => {
                  const individual = getIndividualPermission(selectedEmployee, page.path);
                  const roleBasedAccess = page.allowedRoles.includes(selectedEmployee.role);
                  const effectiveAccess = hasPageAccess(selectedEmployee, page);

                  return (
                    <div
                      key={page.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-sm">{page.name}</div>
                        <div className="text-xs text-gray-500">
                          {page.path} | 권한 기반:{' '}
                          <span className={roleBasedAccess ? 'text-green-600' : 'text-red-600'}>
                            {roleBasedAccess ? '허용' : '거부'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            effectiveAccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {effectiveAccess ? '접근 가능' : '접근 불가'}
                        </span>
                        <select
                          value={individual || 'none'}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'none') {
                              handleRemovePermission(selectedEmployee.id, page.path);
                            } else {
                              handleAddPermission(
                                selectedEmployee.id,
                                page.path,
                                value as PermissionType
                              );
                            }
                          }}
                          className="w-28 px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="none">권한 기반</option>
                          <option value={PermissionType.ALLOW}>개별 허용</option>
                          <option value={PermissionType.DENY}>개별 거부</option>
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 푸터 (하단 고정) */}
            <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setIsPermissionModalOpen(false);
                    setSelectedEmployee(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;
